import { ReportRowSeparator } from "./icons/patientDetailIcons";
import { InlineLoading } from "./loading/LoadingIndicators";
import type { Report } from "../types";
import {
  formatReportDate,
  formatReportTypeLabel,
  getReportResultText,
  getReportResultTone,
  getReportResultToneClassName,
} from "../utils/reportDisplay";

export type SortedReport = Omit<Report, "createdAt"> & {
  reportType: string;
  createdAt: Date;
};

type ReportVariant = "original" | "derivative";
type ReportFetchMode = "view" | "download";

export interface ReportsTabProps {
  sortedReports: SortedReport[];
  reportAnalysis: Record<string, unknown>;
  reportsLoading: boolean;
  reportsError: string | null;
  reportActionError: string | null;
  onReportClick: (reportType: string, fileName: string) => void;
  onFetchReportVariant: (params: {
    reportType: string;
    fileName: string;
    variant?: ReportVariant;
    mode?: ReportFetchMode;
    fallbackFileName?: string;
  }) => void;
}

function getReportAnalysisData(
  report: SortedReport,
  reportAnalysis: Record<string, unknown>
): Record<string, unknown> | null {
  if (report.data && typeof report.data === "object") {
    return report.data as Record<string, unknown>;
  }
  const key = `${report.reportType}_${report._id}`;
  const fromAnalysis = reportAnalysis[key];
  if (fromAnalysis && typeof fromAnalysis === "object") {
    return fromAnalysis as Record<string, unknown>;
  }
  return null;
}

export default function ReportsTab({
  sortedReports,
  reportAnalysis,
  reportsLoading,
  reportsError,
  reportActionError,
  onReportClick,
  onFetchReportVariant,
}: ReportsTabProps) {
  if (reportsLoading) {
    return <InlineLoading message="Loading reports..." />;
  }

  if (reportsError) {
    return <div className="text-sm text-rag-red">{reportsError}</div>;
  }

  if (sortedReports.length === 0) {
    return (
      <div className="text-sm text-guardian-header">No reports available.</div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {reportActionError && (
        <div className="mb-2 shrink-0 text-sm text-rag-red" role="alert">
          {reportActionError}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      {sortedReports.map((report) => {
        const reportAnalysisData = getReportAnalysisData(
          report,
          reportAnalysis
        );
        const resultText = getReportResultText(
          report.reportType,
          reportAnalysisData
        );
        const tone = getReportResultTone(
          report.reportType,
          reportAnalysisData
        );
        const toneClass = getReportResultToneClassName(tone);
        const isChart = report.reportType === "chart";

        return (
          <div
            key={report._id}
            className="cursor-pointer border-b border-guardian-10 py-2.5 transition-colors hover:bg-guardian-10/60"
            onClick={() =>
              onReportClick(report.reportType, report.fileName)
            }
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="shrink-0 text-sm font-medium text-guardian-body">
                  {formatReportTypeLabel(report.reportType)}
                </span>
                {resultText != null && (
                  <>
                    <ReportRowSeparator />
                    <span
                      className={`min-w-0 truncate text-sm font-normal tracking-[-0.013em] ${toneClass}`}
                    >
                      {resultText}
                    </span>
                  </>
                )}
              </div>
              <span className="shrink-0 whitespace-nowrap text-sm font-normal text-guardian-header">
                {formatReportDate(report.createdAt)}
              </span>
            </div>
            {isChart && (
              <div
                className="mt-2 flex w-full flex-wrap gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="rounded-md bg-guardian-10 px-2 py-1 text-xs text-guardian-body transition-colors hover:bg-guardian-20"
                  onClick={() =>
                    onFetchReportVariant({
                      reportType: report.reportType,
                      fileName: report.fileName,
                      variant: "original",
                      mode: "view",
                    })
                  }
                >
                  View Original
                </button>
                <button
                  type="button"
                  className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200 disabled:cursor-not-allowed disabled:bg-guardian-10 disabled:text-guardian-header"
                  disabled={
                    !report.derivativeFileName && !report.derivativeFilePath
                  }
                  title={
                    !report.derivativeFileName && !report.derivativeFilePath
                      ? "Derivative not available"
                      : "View SuperECG"
                  }
                  onClick={() =>
                    onFetchReportVariant({
                      reportType: report.reportType,
                      fileName: report.fileName,
                      variant: "derivative",
                      mode: "view",
                    })
                  }
                >
                  View SuperECG
                </button>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
